import {Minus, Plus} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useFormContext} from "react-hook-form";

import {AsyncSearchCombobox} from "@/components/ui/async-search-combobox";
import {Button} from "@/components/ui/button";
import {FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useGetHasPermissions} from "@/src/modules/core/store/me";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";

import {cn} from "@/lib/utils";

import {useVehicleMakesSearch, VEHICLE_MAKE_SEARCH_MIN_CHARS} from "../hooks/use-vehicle-makes";
import {VEHICLE_FORM_HELP_KEYS} from "../lib/vehicle-help";
import {
  useVehicleModelsSearch,
  VEHICLE_MODEL_SEARCH_MIN_CHARS,
} from "../hooks/use-vehicle-models";
import {vehiclesService} from "../services";
import {VEHICLE_CATALOG_SEARCH_DEBOUNCE_MS} from "../lib/vehicle-catalog-search";
import type {VehicleMake, VehicleMakeModelFormValues, VehicleModel} from "../types";

type VehicleMakeModelFieldsProps = {
  disabled?: boolean;
  /** `pair` — side-by-side on sm+ (property/company sheet grids). Default `stacked`. */
  columns?: "stacked" | "pair";
  /**
   * When set, forces catalog make/model comboboxes on (true) or off (false).
   * When omitted, uses vehicle `store` / `update` permissions (parking enforcement).
   */
  catalogEnabledOverride?: boolean;
};

const MANUAL_TO_CATALOG_DEBOUNCE_MS = VEHICLE_CATALOG_SEARCH_DEBOUNCE_MS;

/** Same catalog label ignoring case/outer spaces (search box vs list). */
function catalogLabelEquals(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function VehicleMakeModelFields({
  disabled,
  columns = "stacked",
  catalogEnabledOverride,
}: VehicleMakeModelFieldsProps) {
  const form = useFormContext<VehicleMakeModelFormValues>();
  const getHasPermission = useGetHasPermissions();
  const inferredCatalogEnabled =
    getHasPermission("vehicle store") ||
    getHasPermission("vehicle update") ||
    getHasPermission("companyvehicle store") ||
    getHasPermission("companyvehicle update");
  const catalogEnabled =
    catalogEnabledOverride !== undefined ? catalogEnabledOverride : inferredCatalogEnabled;

  const makeId = form.watch("vehicle_make_id");
  const makeTextWatch = form.watch("make");
  const modelTextWatch = form.watch("model");

  const [makeSearch, setMakeSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [makeManualOpen, setMakeManualOpen] = useState(false);
  const [modelManualOpen, setModelManualOpen] = useState(false);
  const makeManualOpenRef = useRef(makeManualOpen);
  const modelManualOpenRef = useRef(modelManualOpen);
  makeManualOpenRef.current = makeManualOpen;
  modelManualOpenRef.current = modelManualOpen;

  const makesQuery = useVehicleMakesSearch(makeSearch);
  const modelsQuery = useVehicleModelsSearch(makeId, modelSearch);

  const makes = makesQuery.items;
  const modelsFromApi = modelsQuery.items;
  const models = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (
      makeId != null &&
      Number.isFinite(makeId) &&
      q.length > 0 &&
      q.length < VEHICLE_MODEL_SEARCH_MIN_CHARS
    ) {
      return modelsFromApi.filter((m) => m.name.toLowerCase().includes(q));
    }
    return modelsFromApi;
  }, [modelsFromApi, modelSearch, makeId]);

  const makeListLoading =
    makeSearch.trim().length >= VEHICLE_MAKE_SEARCH_MIN_CHARS &&
    (makesQuery.isFetching || makesQuery.isPending);

  const modelListLoading =
    makeId != null && Number.isFinite(makeId)
      ? modelsQuery.isFetching || modelsQuery.isPending
      : modelSearch.trim().length >= VEHICLE_MODEL_SEARCH_MIN_CHARS &&
        (modelsQuery.isFetching || modelsQuery.isPending);

  const makeItems = makes;

  const modelItems = models;

  const makeEmptyMessage = useMemo(() => {
    if (makesQuery.isError) return "Could not load makes.";
    if (makeSearch.trim().length < VEHICLE_MAKE_SEARCH_MIN_CHARS) {
      return `Type at least ${VEHICLE_MAKE_SEARCH_MIN_CHARS} characters to search.`;
    }
    return "No makes match.";
  }, [makesQuery.isError, makeSearch]);

  const modelEmptyMessage = useMemo(() => {
    const hasMake = makeId != null && Number.isFinite(makeId);
    if (modelsQuery.isError) return "Could not load models.";
    if (!hasMake && modelSearch.trim().length < VEHICLE_MODEL_SEARCH_MIN_CHARS) {
      return `Type at least ${VEHICLE_MODEL_SEARCH_MIN_CHARS} characters to search.`;
    }
    return "No models match.";
  }, [makeId, modelsQuery.isError, modelSearch]);

  const selectedMake: VehicleMake | null = useMemo(() => {
    if (makeId == null || !Number.isFinite(makeId)) return null;
    const fromList = makes.find((m) => m.vehicle_make_id === makeId);
    if (fromList) return fromList;
    const name = makeTextWatch?.trim();
    if (name) return {vehicle_make_id: makeId, name};
    return null;
  }, [makes, makeId, makeTextWatch]);

  const modelIdWatch = form.watch("vehicle_model_id");
  const selectedModel: VehicleModel | null = useMemo(() => {
    if (modelIdWatch == null || !Number.isFinite(modelIdWatch)) return null;
    const fromList = models.find((m) => m.vehicle_model_id === modelIdWatch);
    if (fromList) return fromList;
    const name = modelTextWatch?.trim();
    const mk = makeId;
    if (name && mk != null && Number.isFinite(mk)) {
      return {vehicle_model_id: modelIdWatch, vehicle_make_id: mk, name};
    }
    return null;
  }, [models, modelIdWatch, modelTextWatch, makeId]);

  /** Form `make` / `model` matches a catalog row (after pick or manual field). */
  const makeTextMatchesCatalogExactly = useMemo(() => {
    const t = makeTextWatch?.trim() ?? "";
    if (!t) return false;
    return makes.some((m) => catalogLabelEquals(t, m.name));
  }, [makes, makeTextWatch]);

  const modelTextMatchesCatalogExactly = useMemo(() => {
    const t = modelTextWatch?.trim() ?? "";
    if (!t) return false;
    return models.some((m) => catalogLabelEquals(t, m.name));
  }, [models, modelTextWatch]);

  /**
   * Combobox search text matches a catalog name but user has not selected a row yet.
   * `make`/`model` are only set on selection, so we must also read `makeSearch` / `modelSearch`.
   */
  const makeSearchMatchesCatalogExactly = useMemo(() => {
    const q = makeSearch.trim();
    if (!q) return false;
    return makes.some((m) => catalogLabelEquals(q, m.name));
  }, [makes, makeSearch]);

  const modelSearchMatchesCatalogExactly = useMemo(() => {
    const q = modelSearch.trim();
    if (!q) return false;
    return models.some((m) => catalogLabelEquals(q, m.name));
  }, [models, modelSearch]);

  const makeManualPlusDisabled =
    disabled ||
    selectedMake != null ||
    makeTextMatchesCatalogExactly ||
    makeSearchMatchesCatalogExactly;
  const modelManualPlusDisabled =
    disabled ||
    selectedModel != null ||
    modelTextMatchesCatalogExactly ||
    modelSearchMatchesCatalogExactly;

  const showCatalogMakes = catalogEnabled;
  const showCatalogModels = catalogEnabled && makeId != null && Number.isFinite(makeId);

  const showMakeTextInput = !showCatalogMakes || makeManualOpen;
  const showModelTextInput = !showCatalogModels || modelManualOpen;

  /** Edit / prefill: typed make/model without catalog IDs must stay visible. */
  useEffect(() => {
    if (!showCatalogMakes) return;
    const m = form.getValues("make")?.trim();
    const id = form.getValues("vehicle_make_id");
    if (m && (id == null || !Number.isFinite(id))) {
      setMakeManualOpen(true);
    }
  }, [showCatalogMakes, form]);

  useEffect(() => {
    if (!showCatalogModels) return;
    const mo = form.getValues("model")?.trim();
    const mid = form.getValues("vehicle_model_id");
    if (mo && (mid == null || !Number.isFinite(mid))) {
      setModelManualOpen(true);
    }
  }, [showCatalogModels, form]);

  const openMakeManual = (searchTextOverride?: string) => {
    const searchText = (searchTextOverride ?? makeSearch).trim();
    setMakeManualOpen(true);
    form.setValue("vehicle_make_id", null, {shouldDirty: true});
    form.setValue("vehicle_model_id", null, {shouldDirty: true});
    form.setValue("model", "", {shouldDirty: true});
    if (searchText) {
      form.setValue("make", searchText, {shouldDirty: true});
    }
    setModelManualOpen(false);
    setMakeSearch("");
    setModelSearch("");
  };

  const syncMakeInputFromCatalog = useCallback(
    (row: VehicleMake) => {
      setMakeManualOpen(false);
      setMakeSearch("");
      form.setValue("make", row.name, {shouldDirty: true});
      form.setValue("vehicle_make_id", row.vehicle_make_id, {shouldDirty: true});
      form.setValue("model", "", {shouldDirty: true});
      form.setValue("vehicle_model_id", null, {shouldDirty: true});
      setModelManualOpen(false);
      setModelSearch("");
    },
    [form],
  );

  /** After a pause in typing, if manual make equals exactly one catalog row, sync IDs and return to combobox. */
  useEffect(() => {
    if (!showCatalogMakes || !makeManualOpen || disabled) return;
    const snapshot = makeTextWatch?.trim() ?? "";
    if (snapshot.length < VEHICLE_MAKE_SEARCH_MIN_CHARS) return;

    const t = window.setTimeout(() => {
      if (!makeManualOpenRef.current) return;
      const latest = form.getValues("make")?.trim() ?? "";
      if (latest !== snapshot) return;
      const makeIdNow = form.getValues("vehicle_make_id");
      if (makeIdNow != null && Number.isFinite(makeIdNow)) return;
      void vehiclesService.searchVehicleMakes(latest).then(({data}) => {
        const matches = data.filter((m) => catalogLabelEquals(latest, m.name));
        if (matches.length !== 1) return;
        syncMakeInputFromCatalog(matches[0]!);
      });
    }, MANUAL_TO_CATALOG_DEBOUNCE_MS);

    return () => window.clearTimeout(t);
  }, [
    makeTextWatch,
    makeManualOpen,
    disabled,
    showCatalogMakes,
    form,
    syncMakeInputFromCatalog,
  ]);

  const onMakeComboboxChange = (item: VehicleMake | null) => {
    if (!item) {
      form.setValue("make", "", {shouldDirty: true});
      form.setValue("vehicle_make_id", null, {shouldDirty: true});
      form.setValue("vehicle_model_id", null, {shouldDirty: true});
      form.setValue("model", "", {shouldDirty: true});
      setMakeSearch("");
      setModelSearch("");
      setMakeManualOpen(false);
      setModelManualOpen(false);
      return;
    }
    syncMakeInputFromCatalog(item);
  };

  const openModelManual = (searchTextOverride?: string) => {
    const searchText = (searchTextOverride ?? modelSearch).trim();
    setModelManualOpen(true);
    form.setValue("vehicle_model_id", null, {shouldDirty: true});
    if (searchText) {
      form.setValue("model", searchText, {shouldDirty: true});
    }
    setModelSearch("");
  };

  const onModelComboboxChange = (item: VehicleModel | null) => {
    if (!item) {
      form.setValue("model", "", {shouldDirty: true});
      form.setValue("vehicle_model_id", null, {shouldDirty: true});
      setModelSearch("");
      setModelManualOpen(false);
      return;
    }
    setModelManualOpen(false);
    form.setValue("model", item.name, {shouldDirty: true});
    form.setValue("vehicle_model_id", item.vehicle_model_id, {shouldDirty: true});
  };

  /** After a pause in typing, if manual model equals exactly one row for the current make, sync and return to combobox. */
  useEffect(() => {
    if (!showCatalogModels || !modelManualOpen || disabled) return;
    const makeIdForModels = form.getValues("vehicle_make_id");
    if (makeIdForModels == null || !Number.isFinite(makeIdForModels)) return;
    const snapshot = modelTextWatch?.trim() ?? "";
    if (snapshot.length < VEHICLE_MODEL_SEARCH_MIN_CHARS) return;

    const t = window.setTimeout(() => {
      if (!modelManualOpenRef.current) return;
      const latest = form.getValues("model")?.trim() ?? "";
      if (latest !== snapshot) return;
      const modelIdNow = form.getValues("vehicle_model_id");
      if (modelIdNow != null && Number.isFinite(modelIdNow)) return;
      void vehiclesService
        .searchVehicleModels(makeIdForModels, latest)
        .then(({data}) => {
          const matches = data.filter((m) => catalogLabelEquals(latest, m.name));
          if (matches.length !== 1) return;
          const row = matches[0]!;
          setModelManualOpen(false);
          form.setValue("model", row.name, {shouldDirty: true});
          form.setValue("vehicle_model_id", row.vehicle_model_id, {shouldDirty: true});
        });
    }, MANUAL_TO_CATALOG_DEBOUNCE_MS);

    return () => window.clearTimeout(t);
  }, [modelTextWatch, modelManualOpen, disabled, showCatalogModels, form, makeId]);

  const closeMakeManual = () => {
    setMakeManualOpen(false);
  };

  const closeModelManual = () => {
    setModelManualOpen(false);
  };

  return (
    <div
      className={cn(
        "gap-4",
        columns === "pair" ? "grid grid-cols-1 sm:grid-cols-2" : "flex flex-col",
      )}
    >
      <FormField
        control={form.control}
        name="make"
        render={({field, formState}) => (
          <FormItem className="min-w-0">
            <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.make} showIcon={false}>
              <FormLabel required>Make</FormLabel>
            </FieldHelpLabel>
            {showCatalogMakes && !makeManualOpen ? (
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <AsyncSearchCombobox<VehicleMake>
                    className="w-full"
                    debounceMs={VEHICLE_CATALOG_SEARCH_DEBOUNCE_MS}
                    disabled={disabled}
                    placeholder="Make"
                    emptyMessage={makeEmptyMessage}
                    items={makeItems}
                    value={selectedMake}
                    onChange={(item, _details) => {
                      onMakeComboboxChange(item);
                    }}
                    onSearch={setMakeSearch}
                    isLoading={makeListLoading}
                    getItemKey={(m) => m.vehicle_make_id}
                    getItemLabel={(m) => m.name}
                    isItemEqual={(a, b) => a?.vehicle_make_id === b?.vehicle_make_id}
                    onEnterWithoutMatch={
                      makeManualPlusDisabled ? undefined : openMakeManual
                    }
                  />
                </div>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={makeManualPlusDisabled}
                        className="shrink-0"
                        aria-label={
                          makeManualPlusDisabled && !disabled
                            ? "Make matches catalog—select from the list above"
                            : "Type make manually"
                        }
                        onClick={() => openMakeManual()}
                      >
                        <Plus className="size-4" />
                      </Button>
                    }
                  />
                  <TooltipContent side="top">
                    {makeManualPlusDisabled && !disabled
                      ? "This make is in the catalog—select it from the list."
                      : "Type make manually"}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : null}
            {showCatalogMakes && makeManualOpen ? (
              <div className="flex items-start gap-2">
                <FormControl className="min-w-0 flex-1">
                  <Input
                    {...field}
                    disabled={disabled}
                    placeholder="Enter make"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      const id = form.getValues("vehicle_make_id");
                      if (id != null) {
                        const row = makes.find((m) => m.vehicle_make_id === id);
                        if (
                          !row ||
                          row.name.trim().toLowerCase() !== e.target.value.trim().toLowerCase()
                        ) {
                          form.setValue("vehicle_make_id", null, {shouldDirty: true});
                          form.setValue("vehicle_model_id", null, {shouldDirty: true});
                          form.setValue("model", "", {shouldDirty: true});
                        }
                      }
                    }}
                  />
                </FormControl>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={disabled}
                        className="shrink-0"
                        aria-label="Search makes in catalog"
                        onClick={closeMakeManual}
                      >
                        <Minus className="size-4" />
                      </Button>
                    }
                  />
                  <TooltipContent side="top">Search catalog</TooltipContent>
                </Tooltip>
              </div>
            ) : null}
            {!showCatalogMakes && showMakeTextInput ? (
              <FormControl>
                <Input
                  {...field}
                  disabled={disabled}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    const id = form.getValues("vehicle_make_id");
                    if (id != null) {
                      const row = makes.find((m) => m.vehicle_make_id === id);
                      if (
                        !row ||
                        row.name.trim().toLowerCase() !== e.target.value.trim().toLowerCase()
                      ) {
                        form.setValue("vehicle_make_id", null, {shouldDirty: true});
                        form.setValue("vehicle_model_id", null, {shouldDirty: true});
                        form.setValue("model", "", {shouldDirty: true});
                      }
                    }
                  }}
                />
              </FormControl>
            ) : null}
            {formState.errors.make && <FormError error={formState.errors.make.message} />}
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="model"
        render={({field, formState}) => (
          <FormItem className="min-w-0">
            <FieldHelpLabel helpKey={VEHICLE_FORM_HELP_KEYS.model} showIcon={false}>
              <FormLabel required>Model</FormLabel>
            </FieldHelpLabel>
            {showCatalogModels && !modelManualOpen ? (
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <AsyncSearchCombobox<VehicleModel>
                    className="w-full"
                    debounceMs={VEHICLE_CATALOG_SEARCH_DEBOUNCE_MS}
                    disabled={disabled}
                    placeholder="Model"
                    emptyMessage={modelEmptyMessage}
                    items={modelItems}
                    value={selectedModel}
                    onChange={(item, _details) => {
                      onModelComboboxChange(item);
                    }}
                    onSearch={setModelSearch}
                    isLoading={modelListLoading}
                    getItemKey={(m) => m.vehicle_model_id}
                    getItemLabel={(m) => m.name}
                    isItemEqual={(a, b) => a?.vehicle_model_id === b?.vehicle_model_id}
                    onEnterWithoutMatch={
                      modelManualPlusDisabled ? undefined : openModelManual
                    }
                  />
                </div>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={modelManualPlusDisabled}
                        className="shrink-0"
                        aria-label={
                          modelManualPlusDisabled && !disabled
                            ? "Model matches catalog—select from the list above"
                            : "Type model manually"
                        }
                        onClick={() => openModelManual()}
                      >
                        <Plus className="size-4" />
                      </Button>
                    }
                  />
                  <TooltipContent side="top">
                    {modelManualPlusDisabled && !disabled
                      ? "This model is in the catalog—select it from the list."
                      : "Type model manually"}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : null}
            {showCatalogModels && modelManualOpen ? (
              <div className="flex items-start gap-2">
                <FormControl className="min-w-0 flex-1">
                  <Input
                    {...field}
                    disabled={disabled}
                    placeholder="Enter model"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      const id = form.getValues("vehicle_model_id");
                      if (id != null) {
                        const row = models.find((m) => m.vehicle_model_id === id);
                        if (
                          !row ||
                          row.name.trim().toLowerCase() !== e.target.value.trim().toLowerCase()
                        ) {
                          form.setValue("vehicle_model_id", null, {shouldDirty: true});
                        }
                      }
                    }}
                  />
                </FormControl>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={disabled}
                        className="shrink-0"
                        aria-label="Search models in catalog"
                        onClick={closeModelManual}
                      >
                        <Minus className="size-4" />
                      </Button>
                    }
                  />
                  <TooltipContent side="top">Search catalog</TooltipContent>
                </Tooltip>
              </div>
            ) : null}
            {!showCatalogModels && showModelTextInput ? (
              <FormControl>
                <Input
                  {...field}
                  disabled={disabled}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    const id = form.getValues("vehicle_model_id");
                    if (id != null) {
                      const row = models.find((m) => m.vehicle_model_id === id);
                      if (
                        !row ||
                        row.name.trim().toLowerCase() !== e.target.value.trim().toLowerCase()
                      ) {
                        form.setValue("vehicle_model_id", null, {shouldDirty: true});
                      }
                    }
                  }}
                />
              </FormControl>
            ) : null}
            {formState.errors.model && <FormError error={formState.errors.model.message} />}
          </FormItem>
        )}
      />
    </div>
  );
}
